import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { OrdenFacturacion, OrdenFacturacionState } from '../types';
import { OrdenFacturacionService } from '../services/ordenFacturacionService';

interface OrdenFacturacionActions {
  fetchOrdenes: () => Promise<void>;
  generarOrden: (idParte: number) => Promise<OrdenFacturacion | null>;
  updateEstado: (id: number, estado: string) => Promise<boolean>;
  deleteOrden: (id: number) => Promise<boolean>;
  clearError: () => void;
}

type OrdenFacturacionStore = OrdenFacturacionState & OrdenFacturacionActions;

const initialState: OrdenFacturacionState = {
  ordenes: [],
  loading: false,
  error: null,
};

export const useOrdenFacturacionStore = create<OrdenFacturacionStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      fetchOrdenes: async () => {
        set({ loading: true, error: null });
        try {
          const ordenes = await OrdenFacturacionService.getAll();
          set({ ordenes, loading: false });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Error al cargar órdenes',
            loading: false,
          });
        }
      },

      generarOrden: async (idParte: number) => {
        set({ loading: true, error: null });
        try {
          const orden = await OrdenFacturacionService.generar(idParte);
          const { fetchOrdenes } = get();
          await fetchOrdenes();
          return orden;
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Error al generar orden',
            loading: false,
          });
          return null;
        }
      },

      updateEstado: async (id: number, estado: string) => {
        set({ loading: true, error: null });
        try {
          await OrdenFacturacionService.updateEstado(id, estado);
          const { fetchOrdenes } = get();
          await fetchOrdenes();
          return true;
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Error al actualizar estado',
            loading: false,
          });
          return false;
        }
      },

      deleteOrden: async (id: number) => {
        set({ loading: true, error: null });
        try {
          await OrdenFacturacionService.delete(id);
          const { fetchOrdenes } = get();
          await fetchOrdenes();
          return true;
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Error al eliminar orden',
            loading: false,
          });
          return false;
        }
      },

      clearError: () => set({ error: null }),
    }),
    { name: 'orden-facturacion-store' }
  )
);
